import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ImageMetrics } from '@ai-color-modules/simulation/hooks/useImageMetrics'

/** パターン画像（mask.webp 338B）をデータURLとしてインライン化 */
const MASK_PATTERN_DATA_URL =
  'data:image/webp;base64,UklGRkoBAABXRUJQVlA4WAoAAAASAAAAFwAAFwAAQU5JTQYAAAD/////AABBTk1GlAAAAAAAAAAAABcAABcAAFgCAAJBTFBIOQAAAAEnoJBtBMhf7wAO5NeIiLgEQlEkSU3dKUgGDlAAGta/LDK8I/ovICjywAwcy1sPSw9FT6nY2qcOAABWUDggOgAAADADAJ0BKhgAGAA+kUKcSiWjoqGoCACwEgllAMgSgABAUFAA/teV//npn9bfx/2ffi2KQ2wTn0dAAABBTk1GggAAAAIAAAIAAA8AAA8AAFgCAAJBTFBINQAAAAEnoJBtBMhf7wAO5NeIiLgEQlEkqVGBgssASRSABvyrmmcERPR/AnIW1UiKXu9Xc0iNpOg1AFZQOCAsAAAAkAEAnQEqEAAQAAIANCWgAnS6AAOYAP7guX/2Dv/0Hf/oO/1TP0fXCLICAAA='

/** パターンのタイルサイズ（px） */
const PATTERN_SIZE = 12

interface Point {
  originalX: number
  originalY: number
}

interface PolygonMaskOverlayProps {
  /** ポリゴンの頂点配列（複数ポリゴン対応） */
  polygons: Point[][]
  /** オリジナル画像のサイズ */
  originalDimensions: { width: number; height: number }
  /** 画像の表示メトリクスを取得する関数 */
  getMetrics: () => ImageMetrics
  /** ベースエリアの選択範囲内に限定するかどうか（デフォルト: false） */
  useDetectedAreaOnly?: boolean
  /** ベースマスクURL（外壁+屋根、雨樋・破風は除外済み）。useDetectedAreaOnly=true時に使用 */
  baseMaskUrls?: Record<string, string>
  /** 雨樋マスクURL（カスタムエリアから差し引く用）。useDetectedAreaOnly=true時に使用 */
  gutterMaskUrl?: string
  /** 破風・鼻隠しマスクURL（カスタムエリアから差し引く用、最高優先）。useDetectedAreaOnly=true時に使用 */
  fasciaMaskUrl?: string
  /**
   * 外壁・屋根に統合されたポリゴン（issue #2224）。ベースマスク（外壁∪屋根）へ union する。
   * Python 側は統合ポリゴンをベースマスクに焼き込んだ augmented マスクで ROI を計算するため、
   * オーバーレイも同じ union を適用しないと表示と色変換範囲が一致しない。
   */
  baseMergedPolygons?: Point[][]
  /** 雨樋に統合されたポリゴン（issue #2224）。gutterMaskUrl と同条件（雨樋色指定時のみ）で差し引きに union する */
  gutterMergedPolygons?: Point[][]
  /** 破風・鼻隠しに統合されたポリゴン(issue #2224)。fasciaMaskUrl と同条件で差し引きに union する */
  fasciaMergedPolygons?: Point[][]
}

/**
 * 画像をロードするPromiseを返すヘルパー
 */
const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

/**
 * ポリゴンマスクをCanvasで描画する関数
 */
const drawPolygonMask = (
  ctx: CanvasRenderingContext2D,
  polygons: Point[][],
  width: number,
  height: number,
) => {
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = '#ffffff'
  for (const polygon of polygons) {
    if (polygon.length < 3) continue
    ctx.beginPath()
    ctx.moveTo(polygon[0].originalX, polygon[0].originalY)
    for (let i = 1; i < polygon.length; i++) {
      ctx.lineTo(polygon[i].originalX, polygon[i].originalY)
    }
    ctx.closePath()
    ctx.fill()
  }
}

/**
 * ポリゴン群を白塗りでラスタライズし、ImageData の生データを返す（issue #2224）。
 * 有効なポリゴン（3 頂点以上）が無い場合は null。
 */
const rasterizePolygonData = (
  polygons: Point[][] | undefined,
  width: number,
  height: number,
): Uint8ClampedArray | null => {
  const valid = (polygons ?? []).filter(poly => poly.length >= 3)
  if (valid.length === 0) return null
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  drawPolygonMask(ctx, valid, width, height)
  return ctx.getImageData(0, 0, width, height).data
}

/**
 * 輝度をアルファに変換する関数
 */
const convertLuminanceToAlpha = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  const imageData = ctx.getImageData(0, 0, width, height)
  const { data } = imageData

  for (let i = 0; i < data.length; i += 4) {
    const luminance = data[i] // R (白色なので R=G=B=255 or 0)
    data[i] = 255 // R
    data[i + 1] = 255 // G
    data[i + 2] = 255 // B
    data[i + 3] = luminance // A = 元の輝度
  }

  ctx.putImageData(imageData, 0, 0)
}

/**
 * ポリゴンデータからマスクを動的生成し、パターンオーバーレイを表示するコンポーネント
 *
 * 施主側でカスタムエリアをフォーカスした際に使用。
 *
 * ## SSOT仕様
 * マスク演算仕様: doc/architecture/ai-color-mask-operations.md
 *
 * ## 重要: Python側との一貫性
 * このオーバーレイは「色指定が実際に適用される範囲」を施主に正確に伝える目的がある。
 * 演算式を変更する場合は、Python側（gemini_color_transform_service.py）と
 * 必ず同期すること。
 *
 * ## マスク演算式
 * - useDetectedAreaOnly=true: (((外壁∪統合) ∪ (屋根∪統合)) ∩ ポリゴン) ∩ ¬(雨樋∪統合) ∩ ¬(破風∪統合)
 *   （統合 = 各ベースエリアに統合されたカスタムエリアのポリゴン。issue #2224。
 *     Python 側は統合ポリゴンを焼き込んだ augmented マスクで同じ演算を行う）
 * - useDetectedAreaOnly=false: ポリゴン全体
 */
export const PolygonMaskOverlay: React.FC<PolygonMaskOverlayProps> = ({
  polygons,
  originalDimensions,
  getMetrics,
  useDetectedAreaOnly = false,
  baseMaskUrls,
  gutterMaskUrl,
  fasciaMaskUrl,
  baseMergedPolygons,
  gutterMergedPolygons,
  fasciaMergedPolygons,
}) => {
  const [maskDataUrl, setMaskDataUrl] = useState<string | undefined>()
  const cacheRef = useRef<Record<string, string>>({})

  // ポリゴンデータのキャッシュキーを生成
  const polygonKey = useMemo(() => {
    return JSON.stringify(polygons.map(poly => poly.map(p => `${p.originalX},${p.originalY}`)))
  }, [polygons])

  // baseMaskUrlsのキャッシュキーを生成
  const maskUrlsKey = useMemo(() => {
    if (!baseMaskUrls) return ''
    return JSON.stringify(Object.entries(baseMaskUrls).sort())
  }, [baseMaskUrls])

  // 統合ポリゴン（issue #2224）のキャッシュキーを生成
  const mergedKey = useMemo(() => {
    const serialize = (polys: Point[][] | undefined) =>
      JSON.stringify((polys ?? []).map(poly => poly.map(p => `${p.originalX},${p.originalY}`)))
    return `${serialize(baseMergedPolygons)}__${serialize(gutterMergedPolygons)}__${serialize(
      fasciaMergedPolygons,
    )}`
  }, [baseMergedPolygons, gutterMergedPolygons, fasciaMergedPolygons])

  // フルキャッシュキー
  const cacheKey = useMemo(() => {
    return `${polygonKey}__${useDetectedAreaOnly}__${maskUrlsKey}__${gutterMaskUrl ?? ''}__${
      fasciaMaskUrl ?? ''
    }__${mergedKey}`
  }, [polygonKey, useDetectedAreaOnly, maskUrlsKey, gutterMaskUrl, fasciaMaskUrl, mergedKey])

  // ポリゴンのみのマスクを生成（useDetectedAreaOnly=false時）
  const generatePolygonOnlyMask = useCallback(() => {
    const { width, height } = originalDimensions
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return undefined

    drawPolygonMask(ctx, polygons, width, height)
    convertLuminanceToAlpha(ctx, width, height)

    return canvas.toDataURL('image/png')
  }, [originalDimensions, polygons])

  // ポリゴンとベースマスクのAND演算マスクを生成（useDetectedAreaOnly=true時）
  // 計算式: ((外壁 ∪ 屋根) ∩ ポリゴン) ∩ NOT(雨樋) ∩ NOT(破風)
  const generateAndMask = useCallback(async () => {
    if (!baseMaskUrls || Object.keys(baseMaskUrls).length === 0) {
      // ベースマスクがない場合はポリゴンのみのマスクを返す
      return generatePolygonOnlyMask()
    }

    const { width, height } = originalDimensions

    // 1. ポリゴンマスクを描画
    const polyCanvas = document.createElement('canvas')
    polyCanvas.width = width
    polyCanvas.height = height
    const polyCtx = polyCanvas.getContext('2d')
    if (!polyCtx) return undefined

    drawPolygonMask(polyCtx, polygons, width, height)
    const polyImageData = polyCtx.getImageData(0, 0, width, height)
    const polyData = polyImageData.data

    // 2. ベースマスク（外壁+屋根）をOR合成（ImageDataレベルでmax演算）
    const baseCanvas = document.createElement('canvas')
    baseCanvas.width = width
    baseCanvas.height = height
    const baseCtx = baseCanvas.getContext('2d')
    if (!baseCtx) return undefined

    const baseImageData = baseCtx.getImageData(0, 0, width, height)
    const baseData = baseImageData.data

    for (const url of Object.values(baseMaskUrls)) {
      try {
        const img = await loadImage(url)

        // 一時キャンバスに描画してImageDataを取得
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = width
        tempCanvas.height = height
        const tempCtx = tempCanvas.getContext('2d')
        if (!tempCtx) continue

        tempCtx.drawImage(img, 0, 0, width, height)
        const tempImageData = tempCtx.getImageData(0, 0, width, height)
        const tempData = tempImageData.data

        // OR合成: 各ピクセルの最大値を取る
        for (let i = 0; i < baseData.length; i += 4) {
          baseData[i] = Math.max(baseData[i], tempData[i])
          baseData[i + 1] = Math.max(baseData[i + 1], tempData[i + 1])
          baseData[i + 2] = Math.max(baseData[i + 2], tempData[i + 2])
        }
      } catch {
        // マスク読み込み失敗時は無視して続行
      }
    }

    // 2.5. 外壁・屋根に統合されたポリゴンをベースマスクへ union（issue #2224）。
    // Python 側の augmented マスク（統合ポリゴンを白で焼き込んだ外壁/屋根マスク）と同じ演算にする。
    const baseMergedData = rasterizePolygonData(baseMergedPolygons, width, height)
    if (baseMergedData) {
      for (let i = 0; i < baseData.length; i += 4) {
        baseData[i] = Math.max(baseData[i], baseMergedData[i])
        baseData[i + 1] = Math.max(baseData[i + 1], baseMergedData[i + 1])
        baseData[i + 2] = Math.max(baseData[i + 2], baseMergedData[i + 2])
      }
    }

    // 3. 雨樋マスクをロード（ビット反転用）
    let gutterData: Uint8ClampedArray | null = null
    if (gutterMaskUrl) {
      try {
        const gutterImg = await loadImage(gutterMaskUrl)
        const gutterCanvas = document.createElement('canvas')
        gutterCanvas.width = width
        gutterCanvas.height = height
        const gutterCtx = gutterCanvas.getContext('2d')
        if (gutterCtx) {
          gutterCtx.drawImage(gutterImg, 0, 0, width, height)
          const gutterImageData = gutterCtx.getImageData(0, 0, width, height)
          gutterData = gutterImageData.data
        }
      } catch {
        // 雨樋マスク読み込み失敗時は無視
      }
    }

    // 3.2. 雨樋に統合されたポリゴンを雨樋マスクへ union（issue #2224）。
    // 呼び出し側は gutterMaskUrl と同じ条件（雨樋色指定時のみ）で渡す = augmented 雨樋マスクと同義。
    const gutterMergedData = rasterizePolygonData(gutterMergedPolygons, width, height)
    if (gutterMergedData) {
      if (gutterData) {
        for (let i = 0; i < gutterData.length; i += 4) {
          gutterData[i] = Math.max(gutterData[i], gutterMergedData[i])
        }
      } else {
        gutterData = gutterMergedData
      }
    }

    // 3.5. 破風マスクをロード（ビット反転用、最高優先）
    let fasciaData: Uint8ClampedArray | null = null
    if (fasciaMaskUrl) {
      try {
        const fasciaImg = await loadImage(fasciaMaskUrl)
        const fasciaCanvas = document.createElement('canvas')
        fasciaCanvas.width = width
        fasciaCanvas.height = height
        const fasciaCtx = fasciaCanvas.getContext('2d')
        if (fasciaCtx) {
          fasciaCtx.drawImage(fasciaImg, 0, 0, width, height)
          const fasciaImageData = fasciaCtx.getImageData(0, 0, width, height)
          fasciaData = fasciaImageData.data
        }
      } catch {
        // 破風マスク読み込み失敗時は無視
      }
    }

    // 3.7. 破風に統合されたポリゴンを破風マスクへ union（issue #2224、雨樋と同様）
    const fasciaMergedData = rasterizePolygonData(fasciaMergedPolygons, width, height)
    if (fasciaMergedData) {
      if (fasciaData) {
        for (let i = 0; i < fasciaData.length; i += 4) {
          fasciaData[i] = Math.max(fasciaData[i], fasciaMergedData[i])
        }
      } else {
        fasciaData = fasciaMergedData
      }
    }

    // 4. ImageDataレベルで演算:
    //    結果 = ポリゴン AND ベースマスク AND NOT(雨樋) AND NOT(破風)
    //
    // 閾値について（SSOT仕様: doc/architecture/ai-color-mask-operations.md）:
    // - MASK_THRESHOLD = 127: ベースマスクの二値化閾値
    // - GUTTER_THRESHOLD = 128: 雨樋マスクの閾値（>= 128 が雨樋）
    // - FASCIA_THRESHOLD = 128: 破風マスクの閾値（>= 128 が破風）
    // - baseLuminance > 0: ソフトエッジ保持のため閾値ではなく非ゼロ判定
    //   （オーバーレイ表示でグラデーションを維持する意図的な設計）
    for (let i = 0; i < polyData.length; i += 4) {
      const polyLuminance = polyData[i] // ポリゴン部分は白(255)、外は黒(0)
      const baseLuminance = baseData[i] // ベースマスクの輝度（グレースケール）
      // 雨樋マスクをビット反転: 白(255)→0、黒(0)→255
      // GUTTER_THRESHOLD=128: gutterData >= 128 が雨樋、< 128 が非雨樋
      // NOT演算後: gutterInverted >= 128 が非雨樋（有効領域）
      const gutterInverted = gutterData ? 255 - gutterData[i] : 255
      // 破風マスクをビット反転: 同様に NOT(破風)
      const fasciaInverted = fasciaData ? 255 - fasciaData[i] : 255

      // AND演算: ポリゴン AND ベースマスク AND NOT(雨樋) AND NOT(破風)
      // 全てが条件を満たす場合のみベースマスクの輝度値を採用
      const resultLuminance =
        polyLuminance > 0 && baseLuminance > 0 && gutterInverted >= 128 && fasciaInverted >= 128
          ? baseLuminance
          : 0

      polyData[i] = resultLuminance
      polyData[i + 1] = resultLuminance
      polyData[i + 2] = resultLuminance
      polyData[i + 3] = 255 // アルファは後で輝度から変換
    }

    // 5. 輝度→アルファ変換（ヘルパー関数を再利用）
    polyCtx.putImageData(polyImageData, 0, 0)
    convertLuminanceToAlpha(polyCtx, width, height)

    return polyCanvas.toDataURL('image/png')
  }, [
    baseMaskUrls,
    baseMergedPolygons,
    generatePolygonOnlyMask,
    gutterMaskUrl,
    gutterMergedPolygons,
    fasciaMaskUrl,
    fasciaMergedPolygons,
    originalDimensions,
    polygons,
  ])

  useEffect(() => {
    if (polygons.length === 0 || polygons.every(poly => poly.length < 3)) {
      setMaskDataUrl(undefined)
      return
    }

    // キャッシュにあればそのまま使用
    if (cacheRef.current[cacheKey]) {
      setMaskDataUrl(cacheRef.current[cacheKey])
      return
    }

    // クリーンアップ用フラグ（アンマウント後のsetState防止）
    let cancelled = false

    // マスク生成
    const generate = async () => {
      let dataUrl: string | undefined

      if (useDetectedAreaOnly && baseMaskUrls) {
        dataUrl = await generateAndMask()
      } else {
        dataUrl = generatePolygonOnlyMask()
      }

      if (dataUrl && !cancelled) {
        cacheRef.current[cacheKey] = dataUrl
        setMaskDataUrl(dataUrl)
      }
    }

    void generate()

    return () => {
      cancelled = true
    }
  }, [
    polygons,
    cacheKey,
    useDetectedAreaOnly,
    baseMaskUrls,
    gutterMaskUrl,
    fasciaMaskUrl,
    generateAndMask,
    generatePolygonOnlyMask,
  ])

  const metrics = getMetrics()
  const { dispW, dispH, offX, offY } = metrics

  if (!maskDataUrl) return null

  return (
    <div
      data-testid="polygon-mask-overlay"
      style={
        {
          position: 'absolute',
          left: offX,
          top: offY,
          width: dispW,
          height: dispH,
          backgroundImage: `url(${MASK_PATTERN_DATA_URL})`,
          backgroundSize: `${PATTERN_SIZE}px ${PATTERN_SIZE}px`,
          backgroundRepeat: 'repeat',
          WebkitMaskImage: `url(${maskDataUrl})`,
          maskImage: `url(${maskDataUrl})`,
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          pointerEvents: 'none',
        } as React.CSSProperties
      }
    />
  )
}
